/**
 * uiLanguage
 *
 * Canonical resolver for the *user's UI language* on the webchat
 * (Miles 2026-05-11, thread `C0AM8U804KZ/1778281880.499349`, Bug G).
 *
 * Why this exists
 * ──────────────────────────────────────────────────────────────────
 * Auto-generated thread tab labels were rendering in whatever language
 * the user's *first message* happened to be in — a Spanish first turn
 * on an English-browser session would still produce a Spanish chip
 * label, and vice-versa. Miles flagged this as a UX bug: thread tab
 * labels must always render in the language of the user's UI, not the
 * language of the underlying conversation.
 *
 * The webchat does not have a formal i18n context yet, so this module
 * is the single canonical UI-language source. Everywhere we generate
 * a *user-facing label about the conversation* (thread chip name,
 * thread tooltip summary, etc.) we resolve through `getUserUiLanguage`.
 * If a real i18n context lands later, only this module changes; the
 * call-sites are already routed correctly.
 *
 * Resolution order
 * ──────────────────────────────────────────────────────────────────
 *  1. Explicit override key in `localStorage` (`prest0_ui_language`).
 *     Reserved for the eventual settings-driven override; ignored when
 *     it isn't a recognized 2-letter code.
 *  2. `navigator.language` — `/^es\b/` ⇒ `es`, `/^en\b/` ⇒ `en`. This
 *     mirrors the existing `firestoreUserManager.preferences.language`
 *     fallback so behaviour is consistent across the app.
 *  3. Default `'en'`.
 *
 * Output
 * ──────────────────────────────────────────────────────────────────
 * Two-letter ISO 639-1 code restricted today to `'en' | 'es'` because
 * those are the only languages the title summarizer is set up to
 * honour. New languages plug in here AND at the prompt-pinning side
 * of `inferThreadLabel`.
 */

export type UiLanguage = 'en' | 'es';

/** localStorage key for an explicit UI-language override. */
export const UI_LANGUAGE_OVERRIDE_KEY = 'prest0_ui_language';

/**
 * Recognised two-letter UI-language codes. Kept lowercase. The
 * resolver below normalises any longer form (e.g. `en-US`, `es-MX`)
 * to its 2-letter root before matching.
 */
const RECOGNISED: ReadonlySet<UiLanguage> = new Set(['en', 'es']);

/** True when `value` is one of our recognised UI languages. */
export function isUiLanguage(value: unknown): value is UiLanguage {
  return typeof value === 'string' && RECOGNISED.has(value as UiLanguage);
}

/**
 * Reduce an arbitrary BCP-47 tag (e.g. `en-US`, `es-419`, `ES`) to one
 * of our supported `UiLanguage` codes, or `null` if we don't recognise
 * the root. Case-insensitive.
 */
export function normalizeBcp47ToUiLanguage(tag: unknown): UiLanguage | null {
  if (typeof tag !== 'string' || tag.length === 0) return null;
  const root = tag.trim().toLowerCase().split(/[-_]/)[0];
  return isUiLanguage(root) ? (root as UiLanguage) : null;
}

/**
 * Read the explicit UI-language override from localStorage. Returns
 * `null` when localStorage is unavailable, the key is empty, or the
 * stored value isn't recognised.
 *
 * Wrapped in a try/catch because localStorage access can throw under
 * Safari private mode and inside hard sandboxed iframes.
 */
export function readUiLanguageOverride(): UiLanguage | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(UI_LANGUAGE_OVERRIDE_KEY);
    if (!raw) return null;
    const norm = normalizeBcp47ToUiLanguage(raw);
    return norm;
  } catch {
    return null;
  }
}

/**
 * Read the browser/OS UI language. Returns `null` when navigator is
 * unavailable or the value isn't one we recognise.
 */
export function readNavigatorUiLanguage(): UiLanguage | null {
  try {
    if (typeof navigator === 'undefined') return null;
    // `navigator.languages` is the user's preference-ordered list when
    // available; the first recognised entry wins. Fall back to the
    // single `navigator.language` field on older runtimes.
    const list: readonly string[] = Array.isArray((navigator as Navigator).languages)
      ? (navigator as Navigator).languages as readonly string[]
      : (typeof (navigator as Navigator).language === 'string' ? [(navigator as Navigator).language] : []);
    for (const tag of list) {
      const norm = normalizeBcp47ToUiLanguage(tag);
      if (norm) return norm;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Pure resolver — takes the candidate inputs explicitly and returns
 * the resolved language. Keeps `getUserUiLanguage()` testable without
 * stubbing global `localStorage` / `navigator` directly.
 */
export function resolveUiLanguage(args: {
  override?: UiLanguage | null;
  navigatorLang?: UiLanguage | null;
  fallback?: UiLanguage;
}): UiLanguage {
  if (args.override && isUiLanguage(args.override)) return args.override;
  if (args.navigatorLang && isUiLanguage(args.navigatorLang)) return args.navigatorLang;
  return args.fallback || 'en';
}

/**
 * Canonical UI-language source for the webchat. Every callsite that
 * generates a *user-facing label about the conversation* MUST resolve
 * through this helper, so the language stays consistent app-wide.
 *
 * Safe to call during SSR / non-browser contexts: returns `'en'`.
 */
export function getUserUiLanguage(): UiLanguage {
  return resolveUiLanguage({
    override: readUiLanguageOverride(),
    navigatorLang: readNavigatorUiLanguage(),
    fallback: 'en',
  });
}

export default getUserUiLanguage;
